import React from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";
import "../../styles/main.css"; // Optional for additional styling
import { Heading, TypographyColor, TypographyType, Body, BodyColor, BodyType } from "../../ui-elements";
import { useRecoilState } from "recoil";
import { userCurrencyState } from "../../states";

// Define types
interface ChartData {
  label: string;
  value: number;
  color: string;
  parent?: string;
  parentAverage?: number;
  growthRateText?: string;
}

interface Props {
  coreCategories: ChartData[];
  adjacentCategories: ChartData[];
  geoCode: number;
}
// Format value based on the currency without symbol and decimal points
const formatValue = (value: number, currency: string) => {
  const locales = {
    USD: 'en-US',
    INR: 'en-IN',
    EUR: 'de-DE', // Add more currencies as needed
  };

  const locale = locales[currency as keyof typeof locales] || 'en-US'; // Default to USD
  return new Intl.NumberFormat(locale, {
    maximumFractionDigits: 0, // Remove decimal points
  }).format(value);
};

// Format tooltip value - remove .00 but keep meaningful decimals like 1.72
const formatTooltipValue = (value: number, currency: string) => {
  if (value < 0.01) {
    return '0';
  }
  const locales = {
    USD: 'en-US',
    INR: 'en-IN',
    EUR: 'de-DE',
  };
  const locale = locales[currency as keyof typeof locales] || 'en-US';

  // Check if value is a whole number
  if (Number.isInteger(value)) {
    return new Intl.NumberFormat(locale, {
      maximumFractionDigits: 0,
    }).format(value);
  }

  // For decimal values, show up to 2 decimal places
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
};
const CustomTooltip = ({ active, payload, geoCode }: any) => {
  // Ensure the hook is called unconditionally
  const [selCurrency] = useRecoilState(userCurrencyState);

  // Check if 'catchment' is present in the URL
  const isCatchmentInUrl = window.location.href.toLowerCase().includes('catchment');

  if (active && payload && payload.length) {
    const { payload: data } = payload[0];
    // Show average data if: geoCode !== 1 OR (geoCode === 1 AND catchment is in URL)
    const shouldShowAverageData = geoCode !== 1 || (geoCode === 1 && isCatchmentInUrl);

    return (
      <div
        className="bar-graph-tooltip"
      >
        <div className="w-100 d-flex">
          <div className="col-2 margin-top-2">
            <div style={{ backgroundColor: data.color }} className="height-10px width-10px rounded-circle"></div>
          </div>
          <div className="col-10 d-flex flex-column justify-content-start align-items-start">
            <Heading
              title={`${data.label} PoI: ${formatTooltipValue(data.value, selCurrency)}`}
              type={TypographyType.h5}
              colour={TypographyColor.primary}
            />
            {shouldShowAverageData && data.parent && (
              <>
                <Body
                  type={BodyType.p4}
                  color={BodyColor.secondary}
                  classname="text-start"
                >
                  {data.growthRateText}
                </Body>
              </>
            )}
          </div>
        </div>
        {shouldShowAverageData && data.parent && (
          <>
            <hr className="w-100 my-1" />
            <div className="w-100 d-flex">
              <div className="col-2 margin-top-2">
                <div style={{ backgroundColor: '#DBDBDB' }} className="height-10px width-10px rounded-circle"></div>
              </div>
              <div className="col-10 d-flex flex-column justify-content-start align-items-start">
                <Heading
                  title={`${data.parent} Average: ${formatTooltipValue(data.parentAverage, selCurrency)} `}
                  type={TypographyType.h5}
                  colour={TypographyColor.primary}
                />
              </div>
            </div>
          </>
        )}
      </div>
    );
  }
  return null;
};

export const BarChartSection: React.FC<Props> = ({
  coreCategories = [],
  adjacentCategories = [],
  geoCode,
}) => {
  // Ensure the hook is called unconditionally
  const [selCurrency] = useRecoilState(userCurrencyState);

  // Check if 'catchment' is present in the URL
  const isCatchmentInUrl = window.location.href.toLowerCase().includes('catchment');

  // Show average data if: geoCode !== 1 OR (geoCode === 1 AND catchment is in URL)
  const shouldShowAverageData = geoCode !== 1 || (geoCode === 1 && isCatchmentInUrl);

  const generateCoreAxisLabels = (
    coreCategories: ChartData[],
    adjacentCategories: ChartData[]
  ) => {
    // Safely map values, defaulting to empty arrays if inputs are undefined
    const coreValues = coreCategories?.map((item) => item?.value || 0) || [];
    const coreAverageValues = coreCategories?.map((item) => item?.parentAverage || 0) || [];
    const adjacentValues = adjacentCategories?.map((item) => item?.value || 0) || [];
    const adjacentAverageValues = adjacentCategories?.map((item) => item?.parentAverage || 0) || [];

    // Find the highest value across all datasets
    const highestCoreValue = Math.max(...coreValues);
    const highestCoreAverageValue = Math.max(...coreAverageValues);
    const highestAdjacentValue = Math.max(...adjacentValues);
    const highestAdjacentAverageValue = Math.max(...adjacentAverageValues);

    // Determine the maximum value
    const highestValue = Math.max(
      highestCoreValue,
      highestCoreAverageValue,
      highestAdjacentValue,
      highestAdjacentAverageValue
    );

    if (highestValue <= 0) {
      return { labels: [0, 2, 4, 6, 8, 10], precision: 0 };
    }

    // Calculate rough step size (we want 5 intervals)
    const rawStep = highestValue / 5;

    // Ceil the step to 1 significant figure to get a "nice" step
    const magnitude = Math.pow(10, Math.floor(Math.log10(rawStep)));
    const step = Math.ceil(rawStep / magnitude) * magnitude;

    // Round step to avoid floating point artifacts
    const precision = Math.max(0, -Math.floor(Math.log10(magnitude)));
    const roundedStep = parseFloat(step.toFixed(precision));

    // Generate the axis labels array (6 labels: 0 through 5*step)
    const labels: number[] = [];
    for (let i = 0; i <= 5; i++) {
      labels.push(parseFloat((roundedStep * i).toFixed(precision)));
    }

    return { labels, precision };
  };

  const { labels: coreAxisLabels, precision: axisPrecision } = generateCoreAxisLabels(coreCategories, adjacentCategories);

  return (
    <div className="charts">
      <div className="chart-section border-right">
        <div className="chart-content">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={coreCategories}
              margin={{ top: 10, right: 0, left: 30, bottom: 10 }}
              style={{ background: "none", textAlign: "start" }}
            >
              <XAxis
                dataKey="label"
                tick={{ fontSize: '0.81vw', fontWeight: 600, fill: 'rgba(131, 131, 131, 1)' }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                ticks={coreAxisLabels}
                fontSize='0.81vw'
                fontWeight={600}
                fill="rgba(131, 131, 131, 1)"
                axisLine={false}
                tickLine={false}
                domain={[0, coreAxisLabels[coreAxisLabels.length - 1]]}
                tickFormatter={(value) => value === 0 ? '0' : Number(value).toFixed(axisPrecision)}
              />
              <Tooltip
                content={<CustomTooltip geoCode={geoCode} />}
                formatter={(value: any, parent: string) =>
                  `${formatValue(value, selCurrency)}`
                }
              />
              <Bar
                dataKey="value"
                radius={[15, 15, 15, 15]}
                barSize={!shouldShowAverageData ? 30 : 12}
                name="POI"
                isAnimationActive={false}
              >
                {coreCategories.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={entry.color}
                    style={{
                      cursor: "default",
                      pointerEvents: "none",
                      background: "none",
                    }}
                  />
                ))}
              </Bar>
              {shouldShowAverageData && (
                <Bar
                  dataKey="parentAverage"
                  fill="rgba(219, 219, 219, 1)"
                  radius={[15, 15, 15, 15]}
                  barSize={!shouldShowAverageData ? 30 : 12}
                  name="Average"
                />
              )}
            </BarChart>
          </ResponsiveContainer>
          <Heading
            title="Core Categories PoI Markers"
            type={TypographyType.h5}
            colour={TypographyColor.primary}
            classname="chart-title"
          />
        </div>
      </div>
      <div className="chart-section">
        <div className="chart-content">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={adjacentCategories}
              margin={{ top: 10, right: 30, left: 0, bottom: 10 }}
              style={{ background: "none", textAlign: "start" }}
            >
              <XAxis
                dataKey="label"
                tick={{ fontSize: '0.81vw', fontWeight: 600, fill: 'rgba(131, 131, 131, 1)' }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                hide={true}
                axisLine={false}
                tickLine={false}
                fontSize='0.81vw'
                fontWeight={600}
                fill="rgba(131, 131, 131, 1)"
                ticks={coreAxisLabels}
                domain={[0, coreAxisLabels[coreAxisLabels.length - 1]]}
                tickFormatter={(value) => value === 0 ? '0' : Number(value).toFixed(axisPrecision)}
              />
              <Tooltip
                content={<CustomTooltip geoCode={geoCode} />}
                formatter={(value: any, parent: string) => formatValue(value, selCurrency)}
              />
              <Bar
                dataKey="value"
                radius={[15, 15, 15, 15]}
                barSize={!shouldShowAverageData ? 30 : 12}
                name="POI"
                isAnimationActive={false}
              >
                {adjacentCategories.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={entry.color}
                    style={{
                      cursor: "pointer",
                      pointerEvents: "none",
                      background: "none",
                    }}
                  />
                ))}
              </Bar>
              {shouldShowAverageData && (
                <Bar
                  dataKey="parentAverage"
                  fill="rgba(219, 219, 219, 1)"
                  radius={[15, 15, 15, 15]}
                  barSize={!shouldShowAverageData ? 30 : 12}
                  name="Average"
                />
              )}
            </BarChart>
          </ResponsiveContainer>
          <Heading
            title="Adjacent Categories PoI Markers"
            type={TypographyType.h5}
            colour={TypographyColor.primary}
            classname="chart-title"
          />
        </div>
      </div>
    </div>
  );
};

